import * as React from 'react';
import {
  Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnSizingState,
  SortingState,
  Updater,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { DataTablePagination } from './data-table-pagination';
import { DataTableViewOptions } from './data-table-view-options';
import { Input } from 'components/ui/input';
import { ExportExcel } from 'components/ui/ExportExcel';
import { Skeleton } from './data-table-skeleton';
import { Button } from 'components/ui/ButtonGs';
import { Pagination } from 'interfaces/pagination';
import * as S from './styles';
import type { DataTableAppearance } from './types';
import { DataTableAppearanceProvider } from './appearance-context';

interface ISelectionAction {
  label: string;
  action: (data: any) => void;
  hasPermission: boolean;
  variant?:
    'primary' | 'secondary' | 'tertiary' | 'danger' | 'quartiary' | undefined;
}

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filterBy?: string;
  exportTable?: boolean;
  exportTableData?: any[];
  isLoading?: boolean;
  showControlColumns?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  selectable?: boolean;
  selectionActions?: ISelectionAction[];
  filterLabel?: string;
  exportFileName?: string;
  rowColor?: (row: TData) => string;
  rowClassName?: (row: TData) => string | undefined;
  onTableInstance?: (tableInstance: any) => void;
  pagination?: Partial<Pagination>;
  manualPagination?: boolean;
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  noWrapHeaders?: boolean;
  noWrapCells?: boolean;
  denseRows?: boolean;
  highlightRowOnHover?: boolean;
  resizable?: boolean;
  enableColumnOrdering?: boolean;
  enableColumnPinning?: boolean;
  defaultColumnPinning?: ColumnPinningState;
  columnPinningVersion?: number;
  tableClassName?: string;
  storageKey?: string;
  appearance?: DataTableAppearance;
  alwaysShowPagination?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string;
  onRowMouseEnter?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>
  ) => void;
  onRowMouseMove?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>
  ) => void;
  onRowMouseLeave?: (
    row: TData,
    event: React.MouseEvent<HTMLTableRowElement>
  ) => void;
}

interface StoredTableState {
  sorting?: SortingState;
  columnVisibility?: VisibilityState;
  columnOrder?: ColumnOrderState;
  columnSizing?: ColumnSizingState;
  columnPinning?: ColumnPinningState;
  columnPinningVersion?: number;
}

const readStoredTableState = (
  storageKey?: string
): StoredTableState | undefined => {
  if (!storageKey || typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(`${storageKey}:prefs`);
    return raw ? (JSON.parse(raw) as StoredTableState) : undefined;
  } catch {
    return undefined;
  }
};

const pinnedPositionStyle = <TData, TValue>(
  column: Column<TData, TValue>
): React.CSSProperties | undefined => {
  const pinned = column.getIsPinned();
  if (pinned === 'left') {
    return { left: column.getStart('left') };
  }
  if (pinned === 'right') {
    return { right: column.getAfter('right') };
  }
  return undefined;
};

const pinnedEdgeStyle = <TData, TValue>(
  column: Column<TData, TValue>
): React.CSSProperties | undefined => {
  const pinned = column.getIsPinned();
  if (pinned === 'right' && column.getIsLastColumn('right')) {
    return { boxShadow: '-4px 0 6px -4px rgba(0, 0, 0, 0.2)' };
  }
  return undefined;
};

const isLastLeftPinned = <TData, TValue>(column: Column<TData, TValue>) =>
  column.getIsPinned() === 'left' && column.getIsLastColumn('left');

const isPinned = <TData, TValue>(column: Column<TData, TValue>) =>
  Boolean(column.getIsPinned());

const pinnedWidthStyle = <TData, TValue>(
  column: Column<TData, TValue>
): React.CSSProperties =>
  isPinned(column)
    ? {
        width: column.getSize(),
        minWidth: column.getSize(),
        maxWidth: column.getSize(),
      }
    : {};

const MIN_COLUMN_WIDTH = 120;

const columnMinWidthStyle = <TData, TValue>(
  column: Column<TData, TValue>,
  resizable: boolean
): React.CSSProperties =>
  resizable && column.getCanResize() ? { minWidth: MIN_COLUMN_WIDTH } : {};

const getInitialColumnPinning = (
  defaults: ColumnPinningState | undefined,
  storedState: StoredTableState | undefined,
  version: number | undefined
): ColumnPinningState => {
  const stored = storedState?.columnPinning;
  if (version === undefined || storedState?.columnPinningVersion === version) {
    return (
      stored ?? {
        left: [...(defaults?.left ?? [])],
        right: [...(defaults?.right ?? [])],
      }
    );
  }

  const defaultLeft = defaults?.left ?? [];
  const defaultRight = defaults?.right ?? [];
  const defaultIds = [...defaultLeft, ...defaultRight];
  return {
    left: [
      ...defaultLeft,
      ...(stored?.left ?? []).filter((id) => !defaultIds.includes(id)),
    ],
    right: [
      ...defaultRight,
      ...(stored?.right ?? []).filter((id) => !defaultIds.includes(id)),
    ],
  };
};

const getColumnDefinitionOrder = <TData,>(
  columns: Column<TData, unknown>[]
): string[] =>
  columns.flatMap((column) =>
    column.columns.length
      ? getColumnDefinitionOrder(column.columns)
      : [column.id]
  );

const reconcileColumnOrder = (
  currentOrder: ColumnOrderState,
  definitionOrder: ColumnOrderState
): ColumnOrderState => {
  if (!currentOrder.length) {
    return currentOrder;
  }

  const availableIds = new Set(definitionOrder);
  const presentIds = new Set<string>();
  const nextOrder = currentOrder.filter((id) => {
    if (!availableIds.has(id) || presentIds.has(id)) {
      return false;
    }
    presentIds.add(id);
    return true;
  });

  definitionOrder.forEach((id, definitionIndex) => {
    if (presentIds.has(id)) {
      return;
    }

    const nextKnownId = definitionOrder
      .slice(definitionIndex + 1)
      .find((candidate) => presentIds.has(candidate));
    const insertionIndex = nextKnownId
      ? nextOrder.indexOf(nextKnownId)
      : nextOrder.length;
    nextOrder.splice(insertionIndex, 0, id);
    presentIds.add(id);
  });

  return nextOrder.length === currentOrder.length &&
    nextOrder.every((id, index) => id === currentOrder[index])
    ? currentOrder
    : nextOrder;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  filterBy,
  filterLabel,
  exportTable = false,
  exportTableData = [],
  isLoading = false,
  showControlColumns = true,
  pageSize = 10,
  pageSizeOptions,
  selectable = false,
  selectionActions,
  exportFileName,
  rowColor,
  rowClassName,
  onTableInstance,

  manualPagination = false,
  pagination = {
    page: 1,
    perPage: 10,
  },
  onPaginationChange,
  manualSorting = false,
  sorting: controlledSorting,
  onSortingChange,
  noWrapHeaders = false,
  noWrapCells = false,
  denseRows = false,
  highlightRowOnHover = false,
  resizable = false,
  enableColumnOrdering = false,
  enableColumnPinning = false,
  defaultColumnPinning,
  columnPinningVersion,
  tableClassName = '',
  storageKey,
  appearance = 'default',
  alwaysShowPagination = false,
  stickyHeader = false,
  maxHeight = 'calc(100vh - 320px)',
  onRowMouseEnter,
  onRowMouseMove,
  onRowMouseLeave,
}: DataTableProps<TData, TValue>) {
  const [localSorting, setLocalSorting] = React.useState<SortingState>(
    () => readStoredTableState(storageKey)?.sorting ?? []
  );
  const sorting = manualSorting ? controlledSorting ?? [] : localSorting;
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(
      () => readStoredTableState(storageKey)?.columnVisibility ?? {}
    );
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(
    () => readStoredTableState(storageKey)?.columnOrder ?? []
  );
  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(
    () => readStoredTableState(storageKey)?.columnSizing ?? {}
  );
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(
    () =>
      getInitialColumnPinning(
        defaultColumnPinning,
        readStoredTableState(storageKey),
        columnPinningVersion
      )
  );
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [rowSelection, setRowSelection] = React.useState({});
  const [clientPagination, setClientPagination] = React.useState({
    pageIndex: 0,
    pageSize,
  });

  React.useEffect(() => {
    if (manualPagination) {
      return;
    }

    setClientPagination((currentPagination) =>
      currentPagination.pageSize === pageSize
        ? currentPagination
        : { pageIndex: 0, pageSize }
    );
  }, [manualPagination, pageSize]);

  React.useEffect(() => {
    if (!storageKey) {
      return;
    }
    try {
      window.localStorage.setItem(
        `${storageKey}:prefs`,
        JSON.stringify({
          sorting,
          columnVisibility,
          columnOrder,
          columnSizing,
          columnPinning,
          columnPinningVersion,
        })
      );
    } catch {
      // ignore storage errors
    }
  }, [
    storageKey,
    sorting,
    columnVisibility,
    columnOrder,
    columnSizing,
    columnPinning,
    columnPinningVersion,
  ]);

  const exportData = exportTableData.length ? exportTableData : data;

  const paginationFilter = React.useMemo(
    () => ({
      pageIndex: pagination?.page ? pagination.page - 1 : 0,
      pageSize: pagination.perPage || 10,
    }),
    [pagination?.page, pagination?.perPage]
  );
  const activePagination = manualPagination
    ? paginationFilter
    : clientPagination;

  const setPaginationFilter = React.useCallback(
    (updater: any) => {
      let nextPagination =
        typeof updater === 'function' ? updater(paginationFilter) : updater;

      if (onPaginationChange) {
        onPaginationChange({
          page: nextPagination.pageIndex + 1,
          limit: nextPagination.pageSize,
        });
      }
    },
    [onPaginationChange, paginationFilter]
  );

  const table = useReactTable({
    data,
    columns,
    enableColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: manualSorting ? onSortingChange : setLocalSorting,
    onRowSelectionChange: setRowSelection,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onColumnPinningChange: setColumnPinning,
    onPaginationChange: manualPagination
      ? setPaginationFilter
      : setClientPagination,
    manualPagination,
    manualSorting,
    pageCount: manualPagination ? pagination?.lastPage : undefined,
    defaultColumn: {
      minSize: MIN_COLUMN_WIDTH,
    },
    ...(resizable
      ? {
          enableColumnResizing: true,
          columnResizeMode: 'onChange' as const,
        }
      : {}),

    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnOrder,
      columnSizing,
      columnPinning,
      rowSelection,
      pagination: activePagination,
    },
  });

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    const columnDefinitionOrder = getColumnDefinitionOrder(
      table.getAllColumns()
    );
    setColumnOrder((currentOrder) =>
      reconcileColumnOrder(currentOrder, columnDefinitionOrder)
    );
  }, [columns, table, isLoading]);

  React.useEffect(() => {
    if (onTableInstance) {
      onTableInstance(table); // Passa a instância da tabela para o componente pai
    }
  }, [table, onTableInstance]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-24 text-center font-sans">
        Sem resultados.
      </div>
    );
  }

  return (
    <DataTableAppearanceProvider value={appearance}>
      <S.GlobalDockAttentionBlink />
      <div>
        {(filterBy ||
          !!selectionActions?.length ||
          exportTable ||
          showControlColumns) && (
          <div className="w-full mb-4 flex flex-col md:flex-row gap-3 items-center justify-between">
            {filterBy && (
              <Input
                placeholder={`Filtrar ${
                  filterLabel?.toLowerCase() ?? filterBy.toLowerCase()
                }...`}
                value={
                  (table.getColumn(filterBy)?.getFilterValue() as string) ?? ''
                }
                onChange={(event) =>
                  table.getColumn(filterBy)?.setFilterValue(event.target.value)
                }
                className="max-w-sm focus-visible:ring-primary-500"
              />
            )}

            {!!selectionActions?.length &&
              selectionActions.map((btn) => (
                <Button.Root
                  onClick={() =>
                    btn.action(table.getFilteredSelectedRowModel())
                  }
                  size="sm"
                  disabled={
                    !table.getFilteredSelectedRowModel().rows.length ||
                    !btn.hasPermission
                  }
                  variant={btn.variant}
                >
                  {btn.label ?? 'Action'} (
                  {table.getFilteredSelectedRowModel().rows.length}/
                  {table.getFilteredRowModel().rows.length})
                </Button.Root>
              ))}

            <div className="flex flex-1 gap-3 w-full justify-end">
              {exportTable && (
                <ExportExcel data={exportData} filename={exportFileName} />
              )}
              {showControlColumns && (
                <DataTableViewOptions
                  table={table}
                  ordering={enableColumnOrdering}
                  defaultColumnPinning={defaultColumnPinning}
                />
              )}
            </div>
          </div>
        )}
        <S.TableSurface
          $appearance={appearance}
          $sticky={stickyHeader}
          $maxHeight={maxHeight}
          data-appearance={appearance}
        >
          <Table className={tableClassName}>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSized = columnSizing[header.id] !== undefined;
                    const isPinnedHeader = isPinned(header.column);
                    return (
                      <TableHead
                        key={header.id}
                        className={`text-sm font-GilroyBold ${noWrapHeaders ? 'whitespace-nowrap' : ''} ${
                          resizable ? 'relative' : ''
                        } ${stickyHeader ? 'sticky top-0 z-20 bg-white' : ''} ${
                          isPinnedHeader ? 'sticky z-30 bg-white' : ''
                        } ${
                          isLastLeftPinned(header.column)
                            ? 'pinned-left-edge'
                            : ''
                        }`}
                        style={{
                          ...columnMinWidthStyle(header.column, resizable),
                          ...(resizable && isSized
                            ? { width: header.getSize() }
                            : {}),
                          ...pinnedWidthStyle(header.column),
                          ...pinnedPositionStyle(header.column),
                          ...pinnedEdgeStyle(header.column),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        {resizable && header.column.getCanResize() && (
                          <div
                            data-column-resizer
                            aria-hidden
                            onPointerDown={header.getResizeHandler()}
                            onMouseDown={header.getResizeHandler()}
                            className="absolute top-0 right-0 h-full w-1 cursor-col-resize touch-none select-none bg-transparent transition-colors hover:bg-[#EE7C2C]"
                          />
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="text-xs font-sans">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    onMouseEnter={
                      onRowMouseEnter
                        ? (event) => onRowMouseEnter(row.original, event)
                        : undefined
                    }
                    onMouseMove={
                      onRowMouseMove
                        ? (event) => onRowMouseMove(row.original, event)
                        : undefined
                    }
                    onMouseLeave={
                      onRowMouseLeave
                        ? (event) => onRowMouseLeave(row.original, event)
                        : undefined
                    }
                    className={
                      [
                        appearance === 'default' &&
                        highlightRowOnHover &&
                        !rowColor
                          ? 'group'
                          : '',
                        rowClassName?.(row.original) ?? '',
                      ]
                        .filter(Boolean)
                        .join(' ') || undefined
                    }
                    style={{
                      backgroundColor: rowColor
                        ? rowColor(row.original)
                        : 'transparent',
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`font-GilroySemibold uppercase ${
                          noWrapCells ? 'whitespace-nowrap' : ''
                        } ${denseRows ? 'py-1.5' : ''} ${
                          isPinned(cell.column) ? 'sticky z-10 bg-white' : ''
                        } ${
                          isLastLeftPinned(cell.column)
                            ? 'pinned-left-edge'
                            : ''
                        } ${
                          appearance === 'default' &&
                          highlightRowOnHover &&
                          !rowColor
                            ? 'bg-white transition-colors duration-150 group-hover:bg-[#FFF1E6]'
                            : ''
                        } ${rowClassName?.(row.original) ?? ''}`}
                        style={{
                          ...columnMinWidthStyle(cell.column, resizable),
                          ...pinnedWidthStyle(cell.column),
                          ...pinnedPositionStyle(cell.column),
                          ...pinnedEdgeStyle(cell.column),
                          ...(isPinned(cell.column) && rowColor
                            ? {
                                backgroundColor:
                                  rowColor(row.original) === 'transparent'
                                    ? 'white'
                                    : rowColor(row.original),
                              }
                            : {}),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center font-sans"
                  >
                    Sem resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </S.TableSurface>
        <div
          className={
            appearance === 'default'
              ? 'flex flex-wrap items-center justify-between gap-3 py-4'
              : undefined
          }
        >
          {selectable && (
            <div className="font-GilroyRegular text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length}{' '}
              {`De ${table.getFilteredRowModel().rows.length} linha(s) selecionada(s).`}
            </div>
          )}
          <div className="flex-1">
            <DataTablePagination
              table={table}
              appearance={appearance}
              alwaysShowPagination={alwaysShowPagination}
              pagination={pagination}
              pageSizeOptions={pageSizeOptions}
            />
          </div>
        </div>
      </div>
    </DataTableAppearanceProvider>
  );
}
